// Company fields — the shared vocabulary a form's `companyField` element picks
// from. Each one reads its allowed values from a data source file + one of its
// columns, so the create payload stores **ids**: `dataSource` is the data
// source's `_id` and `dataSourceColumn` a `columnId` from its
// `dataSourceColumnSchema` (both come from GET /datasource/selection, see
// datasourceApi). Contracts confirmed against the backend. The list embeds each
// row's data source WITH its column schema, so `dataSourceColumn` (a bare
// columnId) is resolved to a human column name in the normalizer — the UI never
// sees ids. Every mutation invalidates `CompanyField/LIST`, which both the list
// and the picker feed provide, so a create/edit/duplicate/hide shows up without
// extra wiring.

import { baseApi } from '../baseApi';
import { ROUTES } from '../routes';
import type { FormDateFilter } from './formsApi';

/** POST /company-field body. `description` may be an empty string. */
export interface CreateCompanyFieldPayload {
  fieldTitle: string;
  /** SQL/db column name — required on create and validated there as a bare
   *  lowercase identifier, max 64 chars (see company-fields/CreateCompanyFieldModal). */
  dbColumn: string;
  /** The data source's `_id`. */
  dataSource: string;
  /** A `columnId` from that data source's column schema. */
  dataSourceColumn: string;
  description: string;
}

/** PATCH /company-field/:id body — create's minus `dbColumn`, which is fixed
 *  once the field exists (the edit form shows it disabled). */
export type UpdateCompanyFieldPayload = Omit<CreateCompanyFieldPayload, 'dbColumn'>;

/** One picker row from GET /company-field/selection — what the form builder's
 *  companyField element chooses from. `dataSourceId` is a bare id (no column
 *  schema): resolve its columns through GET /datasource/selection.
 *  `dataSourceColumnId` is the column this field itself reads, which the builder
 *  keeps mandatory and last in `companyFieldDisplayColumns`. */
export interface CompanyFieldOption {
  id: string;
  fieldTitle: string;
  dataSourceId: string;
  dataSourceColumnId: string;
}

interface ApiCompanyFieldOption {
  _id: string;
  fieldTitle: string;
  dataSource: string;
  dataSourceColumn: string;
}

/** The Status filter's confirmed pair — the wire face of the row's `isHidden`. */
export type CompanyFieldStatusFilter = 'active' | 'hidden';

/** POST /company-field/all body. `search` is omitted when the box is empty;
 *  the toolbar-filter params are omitted when their category has no selection.
 *  `lastUpdated` reuses the shared date-preset contract (FormDateFilter;
 *  "Any time" = send nothing). */
export interface CompanyFieldQueryArgs {
  page: number;
  limit: number;
  search?: string;
  status?: CompanyFieldStatusFilter[];
  dataSource?: string[];
  lastUpdated?: FormDateFilter;
  /** yyyy-mm-dd; sent only with CUSTOM. */
  lastUpdatedStart?: string;
  lastUpdatedEnd?: string;
}

export interface CompanyFieldPerson {
  id: string;
  fullName: string;
}

/** One list row, flattened to what the table renders — plus the two source ids
 *  the edit form needs to preselect its dropdowns. */
export interface CompanyField {
  id: string;
  fieldTitle: string;
  dbColumn: string;
  /** `dataSource._id` — what the update payload sends. */
  dataSourceId: string;
  /** `dataSource.schemaName` — what the table shows. */
  dataSourceName: string;
  /** The raw `columnId` — what the update payload sends. */
  dataSourceColumnId: string;
  /** Resolved from the row's own `dataSourceColumnSchema` — what the table shows. */
  dataSourceColumnName: string;
  description: string;
  isHidden: boolean;
  updatedAt: string;
  lastUpdatedBy: CompanyFieldPerson | null;
}

export interface CompanyFieldPage {
  items: CompanyField[];
  total: number;
}

// ---- raw shapes (per the confirmed response) ----
interface ApiCompanyFieldPerson {
  _id: string;
  user: { _id: string; firstName: string; lastName: string };
}

interface ApiCompanyField {
  _id: string;
  fieldTitle: string;
  dbColumn: string;
  dataSource: {
    _id: string;
    schemaName: string;
    dataSourceColumnSchema: { columnId: string; columnName: string; dataType: string }[];
  };
  /** A `columnId` within `dataSource.dataSourceColumnSchema`. */
  dataSourceColumn: string;
  description: string;
  isHidden: boolean;
  updatedAt: string;
  lastUpdatedBy: ApiCompanyFieldPerson | null;
}

type ApiCompanyFieldPage = { items?: ApiCompanyField[]; totalRecords?: number };

const toPerson = (p: ApiCompanyFieldPerson | null): CompanyFieldPerson | null => {
  if (!p) return null;
  const fullName = `${p.user.firstName} ${p.user.lastName}`.trim();
  return fullName ? { id: p._id, fullName } : null;
};

const toCompanyField = (c: ApiCompanyField): CompanyField => ({
  id: c._id,
  fieldTitle: c.fieldTitle,
  dbColumn: c.dbColumn,
  dataSourceId: c.dataSource._id,
  dataSourceName: c.dataSource.schemaName,
  dataSourceColumnId: c.dataSourceColumn,
  // The row carries its source's whole column schema, so the id resolves here
  // rather than needing /datasource/selection.
  dataSourceColumnName:
    c.dataSource.dataSourceColumnSchema.find((col) => col.columnId === c.dataSourceColumn)
      ?.columnName ?? c.dataSourceColumn,
  description: c.description,
  isHidden: c.isHidden,
  updatedAt: c.updatedAt,
  lastUpdatedBy: toPerson(c.lastUpdatedBy),
});

export const companyFieldsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getCompanyFields: build.query<CompanyFieldPage, CompanyFieldQueryArgs>({
      query: (body) => ({ ...ROUTES.companyFields.list, data: body }),
      transformResponse: (res: ApiCompanyFieldPage) => {
        const items = res?.items ?? [];
        return { items: items.map(toCompanyField), total: res?.totalRecords ?? items.length };
      },
      providesTags: [{ type: 'CompanyField', id: 'LIST' }],
    }),
    getCompanyFieldSelection: build.query<CompanyFieldOption[], void>({
      query: () => ({ ...ROUTES.companyFields.selection }),
      transformResponse: (res: ApiCompanyFieldOption[]) =>
        res.map((c) => ({
          id: c._id,
          fieldTitle: c.fieldTitle,
          dataSourceId: c.dataSource,
          dataSourceColumnId: c.dataSourceColumn,
        })),
      // Same tag the four mutations invalidate, so creating/renaming/hiding a
      // company field refreshes this picker too.
      providesTags: [{ type: 'CompanyField', id: 'LIST' }],
    }),
    createCompanyField: build.mutation<{ _id?: string }, CreateCompanyFieldPayload>({
      query: (body) => ({ ...ROUTES.companyFields.create, data: body }),
      invalidatesTags: [{ type: 'CompanyField', id: 'LIST' }],
    }),
    updateCompanyField: build.mutation<
      { _id?: string },
      { id: string; body: UpdateCompanyFieldPayload }
    >({
      query: ({ id, body }) => ({ ...ROUTES.companyFields.update(id), data: body }),
      invalidatesTags: [{ type: 'CompanyField', id: 'LIST' }],
    }),
    duplicateCompanyField: build.mutation<{ _id?: string }, string>({
      query: (id) => ({ ...ROUTES.companyFields.duplicate(id) }),
      invalidatesTags: [{ type: 'CompanyField', id: 'LIST' }],
    }),
    setCompanyFieldStatus: build.mutation<{ _id?: string }, { id: string; isHidden: boolean }>({
      query: ({ id, isHidden }) => ({
        ...ROUTES.companyFields.status(id),
        data: { isHidden },
      }),
      invalidatesTags: [{ type: 'CompanyField', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetCompanyFieldsQuery,
  useGetCompanyFieldSelectionQuery,
  useCreateCompanyFieldMutation,
  useUpdateCompanyFieldMutation,
  useDuplicateCompanyFieldMutation,
  useSetCompanyFieldStatusMutation,
} = companyFieldsApi;
